import React, { useEffect, useMemo, useState } from "react";
import { X, ChevronLeft, Download } from "lucide-react";
import * as XLSX from "xlsx"; 
import Mark from "mark.js"; 
import { renderAsync } from "docx-preview"; 

// Simplified specific extension parser
function getExt(filename = "") {
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
}

// Clean chunk for URL #search=
function cleanSourceText(rawText) {
  if (!rawText) return "";
  return rawText.replace(/\\[nt]/g, " ").replace(/[\n\t]/g, " ").replace(/[•●○■*`#_""'']/g, "").replace(/\s+/g, " ").trim();
}

function buildPdfUrl(url, page, chunk) {
  let finalUrl = url;
  const fragments = [];
  
  if (page && page !== "Unknown") fragments.push(`page=${page}`);
  
  if (chunk) {
    const words = cleanSourceText(chunk).split(" ").filter(w => w.length > 3);
    let search = words.length >= 4 ? `${words[0]} ${words[1]} ${words[2]} ${words[3]}` : words.join(" ");
    if (search) fragments.push(`search=${encodeURIComponent(search)}`);
  }

  if (fragments.length > 0) finalUrl += `#${fragments.join("&")}`;
  return finalUrl;
}

// DOCX Viewer
function DocxViewer({ url, highlightText }) {
  const [isRendered, setIsRendered] = useState(false);
  const containerRef = React.useRef(null);

  useEffect(() => {
    if (!url || !containerRef.current) return;
    fetch(url).then(r => r.blob()).then(blob => {
      renderAsync(blob, containerRef.current, null, { className: "docx", inWrapper: true }).then(() => setIsRendered(true));
    });
  }, [url]);

  useEffect(() => {
    if (!isRendered || !highlightText || !containerRef.current) return;
    const markInstance = new Mark(containerRef.current);
    markInstance.mark(cleanSourceText(highlightText), {
      element: "mark", className: "bg-yellow-300 rounded-sm", accuracy: "partially", acrossElements: true,
      done: (matches) => { if(matches > 0) setTimeout(() => containerRef.current.querySelector("mark")?.scrollIntoView({behavior: "smooth", block: "center"}), 200); }
    });
    return () => markInstance.unmark();
  }, [isRendered, highlightText]);

  return (
    <div className="h-full overflow-auto bg-gray-200 p-4 relative">
      {!isRendered && <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10 text-gray-500 animate-pulse">Loading DOCX…</div>}
      <div ref={containerRef} className="mx-auto bg-white p-8 shadow-sm" style={{ width: "fit-content", minWidth: "800px", minHeight: "100%" }} />
    </div>
  );
}

// Excel Viewer
function ExcelViewer({ url, highlightText }) {
  const [html, setHtml] = useState("");
  const containerRef = React.useRef(null);
  
  useEffect(() => {
    if (!url) return;
    fetch(url).then(r => r.arrayBuffer()).then(buffer => {
      const wb = XLSX.read(buffer, { type: "array" });
      let htmlStr = XLSX.utils.sheet_to_html(wb.Sheets[wb.SheetNames[0]], { header: "", footer: "" });
      setHtml(htmlStr.replace('<table', '<table class="w-full text-left border-collapse text-sm"'));
    });
  }, [url]);
  
  useEffect(() => {
    if (!html || !highlightText || !containerRef.current) return;
    const markInstance = new Mark(containerRef.current);
    markInstance.mark(cleanSourceText(highlightText), {
      element: "mark", className: "bg-yellow-300", accuracy: "partially",
      done: (matches) => { if(matches > 0) setTimeout(() => containerRef.current.querySelector("mark")?.scrollIntoView({behavior: "smooth", block: "center"}), 100); }
    });
    return () => markInstance.unmark();
  }, [html, highlightText]);

  if (!html) return <div className="p-4 text-gray-500 animate-pulse">Loading Excel…</div>;
  return <div ref={containerRef} className="p-6 h-full overflow-auto bg-white" dangerouslySetInnerHTML={{ __html: html }} />;
}

const QuestionnaireSourceViewModal = ({ isOpen, onClose, data }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      document.body.style.overflow = "hidden";
    } else {
      const t = setTimeout(() => setIsVisible(false), 300);
      document.body.style.overflow = "unset";
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  if (!isVisible && !isOpen) return null;

  // STRICTLY USE THE EXACT KEYS FROM YOUR CONSOLE LOG
  const url = data?.file_url || "";
  const name = data?.file_name || "Document";
  const ext = getExt(name);
  const page = data?.page_number;
  const chunk = data?.source_chunk || "";

  return (
    <div className="fixed inset-0 z-[9999] flex justify-end font-outfit">
      <div className={`absolute inset-0 bg-black/30 transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0"}`} onClick={onClose} />

      <div className={`relative w-full lg:w-[55%] h-full bg-white shadow-2xl transition-transform duration-300 flex flex-col ${isOpen ? "translate-x-0" : "translate-x-full"}`}>
        
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white shadow-sm z-20">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full text-gray-500"><ChevronLeft className="w-5 h-5" /></button>
            <div className="flex flex-col">
              <h3 className="text-sm font-semibold text-gray-900">{name}</h3>
              <span className="text-xs text-gray-500">
                {ext ? ext.toUpperCase() : "FILE"} {page && page !== "Unknown" ? `• Page ${page}` : ""}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {url && <a href={url} target="_blank" rel="noreferrer" className="p-2 hover:bg-gray-100 rounded-full text-gray-500"><Download className="w-5 h-5" /></a>}
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full text-gray-400"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {/* Viewer Area */}
        <div className="flex-1 bg-gray-100 relative overflow-hidden flex flex-col">
          {!url ? (
            <div className="flex items-center justify-center h-full text-red-400 font-medium">Failed to load File URL</div>
          ) : ext === "pdf" ? (
            <iframe src={buildPdfUrl(url, page, chunk)} className="w-full h-full border-none bg-white" title="PDF Viewer" />
          ) : ext === "docx" || ext === "doc" ? (
            <DocxViewer url={url} highlightText={chunk} />
          ) : ext === "xlsx" || ext === "xls" || ext === "csv" ? (
            <ExcelViewer url={url} highlightText={chunk} />
          ) : (
            <iframe src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`} className="w-full h-full border-none bg-white" title="Office Viewer" />
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionnaireSourceViewModal;
